/**
 * Agent knowledge retrieval defaults for the bot-setting feature.
 *
 * Only consumed by `./index.js` — do not import this file from views outside
 * `botSetting`; use the facade default export instead.
 */

/** `retrival_alg` values returned by the robots / resource APIs. */
export const RETRIEVAL_ALG_NUM = Object.freeze({
  NORMAL: 1,
  STRUCTURED: 2,
  TREE: 3
});

/** Per-KB defaults inside `agent_knowledgebase_config.settings` (and UI fallbacks). */
export const KB_SETTINGS_DEFAULTS = Object.freeze({
  semanticProportion: 50,
  thresholdSimi: 65,
  maxReferenceNumber: 3,
  referencesMaxFolders: 5,
  maxReferencesPerFolder: 3,
  referenceQuestionsThreshold: 65,
  leafNodeMaxReferenceNumber: 3
});

/** Default per-KB correlation as 0–1 when API value is missing (`thresholdSimi / 100`). */
export const DEFAULT_PER_KB_CORRELATION_DECIMAL =
  KB_SETTINGS_DEFAULTS.thresholdSimi / 100;

/**
 * Flat fields edited in the retrieval dialog (subset overlaps API flat + chunk pre-filter).
 * Copy in component `setup` so instances do not share one object.
 */
export const EDIT_KNOWLEDGE_FORM_DEFAULTS = Object.freeze({
  is_recall_filter: false,
  semantic_proportion: KB_SETTINGS_DEFAULTS.semanticProportion,
  correlation_threshold: KB_SETTINGS_DEFAULTS.thresholdSimi,
  diff_threshold: 0.4,
  ratio_threshold: 0.15,
  max_reference_knowledge_num: KB_SETTINGS_DEFAULTS.maxReferenceNumber,
  max_reference_folder_num: KB_SETTINGS_DEFAULTS.referencesMaxFolders,
  max_reference_single_folder_num: KB_SETTINGS_DEFAULTS.maxReferencesPerFolder,
  reference_questions_threshold:
    KB_SETTINGS_DEFAULTS.referenceQuestionsThreshold,
  rerank_model: '',
  small_to_big: false
});

/** Flat `botInfo` keys cleared when no knowledge bases remain (matches save surface). */
export const AGENT_KB_PUBLIC_FLAT_KEYS = Object.freeze([
  'retrival_alg',
  'topk',
  'threshold_rerank',
  'rerank_model',
  'enable_rerank',
  'small_to_big',
  'meta_retrieve',
  'is_recall_filter',
  'recall_filter_x',
  'recall_filter_y'
]);

export function isValidRetrivalAlgEnum(n) {
  const v = Number(n);
  return (
    v === RETRIEVAL_ALG_NUM.NORMAL ||
    v === RETRIEVAL_ALG_NUM.STRUCTURED ||
    v === RETRIEVAL_ALG_NUM.TREE
  );
}

/**
 * Keys that may appear on `knowledge_list` items from catalog / unbind-list APIs
 * but must not drive agent retrieval UI until hydrated from `settings` rows.
 * Otherwise `getCorrelationDisplay` reads catalog `correlation_threshold` (e.g. 81 → 0.81).
 */
export const AGENT_KB_LIST_ITEM_LEAK_KEYS = Object.freeze([
  'correlation_threshold',
  'threshold_simi',
  'semantic_proportion',
  'max_reference_knowledge_num',
  'max_reference_folder_num',
  'max_reference_single_folder_num',
  'reference_questions_threshold'
]);

/**
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown>}
 */
export function stripCatalogRetrievalFieldsFromKnowledgeItem(item) {
  if (!item || typeof item !== 'object') return item;
  const out = { ...item };
  for (const k of AGENT_KB_LIST_ITEM_LEAK_KEYS) {
    if (k in out) delete out[k];
  }
  return out;
}

/**
 * One default `settings` row for a knowledge base for the given `retrival_alg`.
 * @param {number} retrivalAlg
 * @param {{ id?: *, knowledge_base_id?: *, knowledge_base_name?: string }} kb
 */
export function buildDefaultAgentKbSettingsRow(retrivalAlg, kb) {
  const id = kb.id ?? kb.knowledge_base_id;
  const name = kb.knowledge_base_name || '';
  const d = KB_SETTINGS_DEFAULTS;
  const base = {
    semantic_proportion: d.semanticProportion,
    knowledge_base_id: id,
    knowledge_base_name: name
  };
  if (retrivalAlg === RETRIEVAL_ALG_NUM.NORMAL) {
    return {
      ...base,
      threshold_simi: d.thresholdSimi,
      max_reference_number: d.maxReferenceNumber
    };
  }
  if (retrivalAlg === RETRIEVAL_ALG_NUM.STRUCTURED) {
    return {
      ...base,
      threshold_simi: d.thresholdSimi,
      references_max_folders: d.referencesMaxFolders,
      max_references_per_folder: d.maxReferencesPerFolder
    };
  }
  return {
    ...base,
    threshold_simi: d.thresholdSimi,
    reference_questions_threshold: d.referenceQuestionsThreshold,
    leaf_node_max_reference_number: d.leafNodeMaxReferenceNumber
  };
}
